import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useFetch from "../../utils/useFetch";
import apiServer from "../../constants/apiServer";
import { del } from "../../utils/api";
import DataList from "../../common/DataList";
import defaultImg from "../../image/default-image.png";
import ConfirmationPopup from "../../common/ConfirmationPopup";
import { img_url_api } from "../../constants/apiImg";
import "../../css/artist.sass";
import { Headers } from "../../constants/Headers";
import TableAction from "../../common/TableAction";
// import { DeleteRow } from "../../utils/commonFuncs";
import { useContext } from "react";
import { DataContext } from "../../context/dataProvider";
import { useLocation } from "react-router-dom";
import { commafy } from "../../utils";

const ProductList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const { currentPageList, setCurrentPageList } = useContext(DataContext);

    const [page, setPage] = useState(currentPageList["product"]);
    const [rowPerPage, setRowPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [actionStatus, setActionStatus] = useState("");
    const [actionMessage, setActionMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [getListError, setGetListError] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [productId, setProductId] = useState("");
    const [headerInfo, setHeaderInfo] = useState(Headers["Product"]);
    const [searchInput, setSearchInput] = useState("");

    // Get search term from URL
    const urlSearchTerm = searchParams.get('search') || '';

    // Initialize search input from URL
    useEffect(() => {
        setSearchInput(urlSearchTerm);
    }, [urlSearchTerm]);

    const { data, totalCount, isLoading, error } = useFetch(
        urlSearchTerm 
            ? `${apiServer.product.list}?page_at=${page}&row_count=${rowPerPage}&search=${encodeURIComponent(urlSearchTerm)}`
            : `${apiServer.product.list}?page_at=${page}&row_count=${rowPerPage}`
    );


    const handleSearchSubmit = () => {
        // Update URL with search parameter
        const newParams = new URLSearchParams(searchParams);
        
        if (searchInput.trim()) {
            newParams.set('search', searchInput.trim());
        } else {
            newParams.delete('search');
        }
        
        // Reset to page 1 when searching
        newParams.set('page_at', '1');
        
        setSearchParams(newParams);
        setPage(1);
        setCurrentPageList(prev => ({ ...prev, product: 1 }));
    };

    const handleInputChange = (value) => {
        setSearchInput(value);
    };

    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading]);

    const modifyData = useCallback(
        (listData) => {
            const modifyData = listData.map((info, index) => {
                const productName = info?.name;
                const categoryName = info?.category?.name;
                const currency = info?.currency?.name || "MMK";
                const firstImage = info?.media?.find(m => m.media_type === 'image')?.s3_url;
                
                return {
                    ...info,
                    rowId: index + 1 + (page - 1) * rowPerPage,
                    product_img: firstImage,
                    product_eng_name: productName?.en || productName?.mm || 'Unknown Product',
                    product_category: categoryName?.eng || categoryName?.mm || 'Unknown Category',  // Show English first
                    currency: currency,
                    price: commafy(info?.price),
                };
            });
            return modifyData;
        },
        [page, rowPerPage]
    );

    const modifyHeader = useCallback(() => {
        headerInfo[1]["renderCell"] = (params) => {
            const profile = params.formattedValue;
            const id = params.api.getRowIndex(params.row.id);
            return (
                <img
                    src={
                        profile === "" || profile === undefined
                            ? defaultImg
                            : img_url_api + profile
                    }
                    alt="product_img"
                    className={
                        profile === "" || profile === undefined
                            ? "dummy_profile"
                            : id === 0
                            ? "first--artist_profile"
                            : "artist_profile"
                    }
                />
            );
        };

        headerInfo[headerInfo.length - 1]["renderCell"] = ({ id }) => {
            const onEdit = () => {
                navigate(`${id}/edit`, { replace: true });
            };
            const onDelete = async () => {
                setConfirmDelete(true);
                setProductId(id);
            };
            const onView = (productId) => {
                navigate(`/processes?productId=${productId}`, { replace: true });
            };
            const onCreate = () => {
                navigate(`/processes/create?productId=${id}`, { replace: true });
            };
            return (
                <TableAction 
                    onEdit={onEdit} 
                    onDelete={onDelete} 
                    onView={onView}
                    onCreate={onCreate}
                    productId={id}
                    showDropdown={true}
                />
            );
        };

        setHeaderInfo(headerInfo);
    }, [headerInfo, navigate]);

    useEffect(() => {
        const modifyDataResult = modifyData(data);
        setRowData(modifyDataResult);
    }, [data, page, modifyData]);

    useEffect(() => {
        if (location?.state !== null) {
            const { status, message } = location?.state;
            setActionStatus(status);
            setActionMessage(message);
        }
    }, [location]);

    useEffect(() => {
        setTotalRecords(totalCount);
    }, [totalCount]);

    useEffect(() => {
        setGetListError(error);
    }, [error, urlSearchTerm, data, totalCount, isLoading]);

    useEffect(() => {
        modifyHeader();
    }, [headerInfo, navigate, modifyHeader]);

    const handleConfirmDelete = async () => {
            setConfirmDelete(false);
            setLoading(true);
            try {
                const { data: deletedInfo } = await del(
                    `${apiServer.product.delete}/${productId}`
                );

                if (deletedInfo.success) {
                    setActionStatus("success");
                    setActionMessage(deletedInfo.message);
                
                    const updatedRows = rowData.filter(row => row.id !== productId);
                    setRowData(updatedRows);
                    
                    setTotalRecords(prev => prev - 1);

                    if (updatedRows.length === 0 && page > 1) {
                        setPage(page - 1);
                        setCurrentPageList(prev => ({ ...prev, product: page - 1 }));
                    }
                } else {
                    setActionStatus("error");
                    setActionMessage(deletedInfo.message || "Failed to delete product");
                }
                setLoading(false);
            } catch (err) {
                setLoading(false);
                setActionStatus("error");
                setActionMessage(err.response?.data?.message || "Failed to delete product");
            }
    };

    const handleChangePage = (currentPage) => {
        setPage(currentPage + 1);
        setCurrentPageList({
            ...currentPageList,
            product: currentPage + 1,
        });
    };

    const handleChangePageSize = (rows) => {
        setRowPerPage(rows);
    };

    return (
        <>
            <DataList
                title={"Product List"}
                headers={headerInfo}
                rowData={rowData}
                isLoading={loading}
                totalCount={totalRecords}
                error={getListError}
                currentPage={page}
                pageSize={rowPerPage}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangePageSize}
                deleteMsg={actionMessage}
                deleteStatus={actionStatus}
                emptyDataText={'There is no product. Please Create One.'}
                onSearchChange={handleInputChange}
                onSearchSubmit={handleSearchSubmit}
                searchPlaceholder="Search products..."
                searchIndicator={urlSearchTerm}
                onClearSearch={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('search');
                  setSearchParams(newParams);
                  setSearchInput('');
                }}
            />

            {confirmDelete && (
                <ConfirmationPopup
                    headerText="Delete Product"
                    body={"Are you sure you want to delete this product?"}
                    onSubmit={handleConfirmDelete}
                    onCancel={() => {
                        setConfirmDelete(false);
                    }}
                />
            )}
        </>
    );
};

export default ProductList;
